import {
  useEffect,
  useState,
  FC,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { ProgressBar } from '@cfa/react-core';
import { useOktaAuth } from '@okta/okta-react';
import { useLocationStore } from '../../../../stores/locationStore';
import { callEvent } from '../../../../utils/Amplitude/AmplitudeUtilities';
import { useSignout } from '../../../../user/useSignout';
import { UserContext } from '../../../../user/UserContext';
import { UserContextType } from '../../../../@types/user';
import SharedModal from '../SharedModal';
import { TypographyStyled } from '../../../Styled/Typography';
import { themeSizes } from '../../../../styles/globalStyleVars';
import {
  markSessionTimeoutPromptActive,
  clearSessionTimeoutPrompt,
} from '../../../../utils/auth/sessionTimeoutPrompt';

const sessionActionKey = 'session-action';

type SessionAction = {
  type: 'still-here' | 'logout';
  timestamp: number;
};

const SessionTimeoutModal: FC = () => {
  const { oktaAuth } = useOktaAuth();
  const { setIsSessionTimeout, setIsFormDirty } = useContext(
    UserContext
  ) as UserContextType;
  const { handleLogout } = useSignout();

  const timeout = 1800000; // 30 min
  const promptBeforeIdle = 60000; // 60 seconds

  const [tillLogout, setTillLogout] = useState<number>(100);
  const [showTimedOutModal, setShowTimedOutModal] = useState<boolean>(false);
  const { activeLocation } = useLocationStore();
  const isHandlingModalRef = useRef(false);
  const isLoggingOutRef = useRef(false);

  const onIdle = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    setShowTimedOutModal(false);
    clearSessionTimeoutPrompt();
    setIsFormDirty(false);

    // Small delay to allow React to finish processing state updates
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      await handleLogout();
    } catch (error) {
      console.error('[onIdle] handleLogout failed, forcing redirect:', error);
      window.location.href = window.location.origin + '/';
    }
  }, [setIsFormDirty, handleLogout]);

  const onPrompt = useCallback(() => {
    markSessionTimeoutPromptActive();

    setShowTimedOutModal(true);
    if (activeLocation) {
      callEvent('session timeout prompt', {
        locationNum: activeLocation ?? 'No location selected',
      });
    }
  }, [activeLocation]);

  const { getRemainingTime, reset, activate, isPrompted } = useIdleTimer({
    onIdle,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
    crossTab: true,
    syncTimers: 200,
  });

  // Safari-specific: Manual check for prompt state and timeout
  useEffect(() => {
    const manualCheck = setInterval(() => {
      const remaining = getRemainingTime();
      const shouldPrompt = remaining <= promptBeforeIdle && remaining > 0;

      if (shouldPrompt && !showTimedOutModal && !isPrompted()) {
        onPrompt();
      }

      if (remaining <= 0 && !isLoggingOutRef.current) {
        isLoggingOutRef.current = true;
        setShowTimedOutModal(false);
        clearSessionTimeoutPrompt();
        setIsFormDirty(false);

        setTimeout(async () => {
          try {
            await handleLogout();
          } catch (error) {
            console.error(
              '[MANUAL CHECK] handleLogout failed, forcing redirect:',
              error
            );
            window.location.href = window.location.origin + '/';
          }
        }, 100);
      }
    }, 1000);

    return () => clearInterval(manualCheck);
  }, [
    showTimedOutModal,
    onPrompt,
    getRemainingTime,
    promptBeforeIdle,
    isPrompted,
    setIsFormDirty,
    setIsSessionTimeout,
    handleLogout,
  ]);

  // When tab becomes visible, do not renew tokens immediately
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Prevent multiple simultaneous handlers
        if (isHandlingModalRef.current) {
          return;
        }

        isHandlingModalRef.current = true;

        // Wait a bit for state to settle
        await new Promise(resolve => setTimeout(resolve, 100));

        const remaining = getRemainingTime();

        // If we're in the prompt window, show modal
        if (remaining <= promptBeforeIdle && remaining > 0) {
          markSessionTimeoutPromptActive();

          setShowTimedOutModal(true);
          if (activeLocation) {
            callEvent('session timeout prompt', {
              locationNum: activeLocation ?? 'No location selected',
            });
          }

          isHandlingModalRef.current = false;
          return;
        }

        // If timeout already passed, logout immediately
        if (remaining <= 0) {
          if (isLoggingOutRef.current) {
            isHandlingModalRef.current = false;
            return;
          }
          isLoggingOutRef.current = true;

          clearSessionTimeoutPrompt();
          setIsFormDirty(false);
          setIsSessionTimeout(true);

          try {
            await handleLogout();
          } catch (error) {
            console.error('Failed to logout on visibility change:', error);
            window.location.href = window.location.origin + '/';
          }

          isHandlingModalRef.current = false;
          return;
        }

        isHandlingModalRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [
    getRemainingTime,
    promptBeforeIdle,
    activeLocation,
    setIsFormDirty,
    setIsSessionTimeout,
    handleLogout,
  ]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== sessionActionKey || !e.newValue) return;

      try {
        const action = JSON.parse(e.newValue) as SessionAction;

        if (action.type === 'still-here') {
          clearSessionTimeoutPrompt();
          activate();
          setShowTimedOutModal(false);
        } else if (action.type === 'logout') {
          if (isLoggingOutRef.current) {
            return;
          }
          isLoggingOutRef.current = true;

          clearSessionTimeoutPrompt();
          setIsFormDirty(false);
          handleLogout().catch(error => {
            console.error('Failed to logout in storage change handler:', error);
            window.location.href = window.location.origin + '/';
          });
        }
      } catch (error) {
        console.error('Failed to parse session action:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [activate, setIsFormDirty, handleLogout]);

  useEffect(() => {
    if (!showTimedOutModal) return;

    const interval = setInterval(() => {
      if (isLoggingOutRef.current) return;
      setTillLogout(Math.round((getRemainingTime() / promptBeforeIdle) * 100));
    }, 500);

    return () => clearInterval(interval);
  }, [showTimedOutModal, getRemainingTime, promptBeforeIdle]);

  const logout = async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    callEvent('clicked logout session timeout modal', {
      locationNum: activeLocation,
    });

    clearSessionTimeoutPrompt();
    setIsFormDirty(false);

    localStorage.setItem(
      sessionActionKey,
      JSON.stringify({
        type: 'logout',
        timestamp: Date.now(),
      })
    );

    setTimeout(() => {
      localStorage.removeItem(sessionActionKey);
    }, 100);

    try {
      await handleLogout();
    } catch (error) {
      console.error('Failed to logout during session timeout:', error);
      window.location.href = window.location.origin + '/';
    }
  };

  const handleCloseModal = async (btnClick?: boolean) => {
    if (isLoggingOutRef.current) {
      return;
    }

    if (btnClick !== true) {
      return;
    }

    clearSessionTimeoutPrompt();
    setShowTimedOutModal(false);

    callEvent('clicked still here session timeout modal', {
      locationNum: activeLocation ?? 'No location selected',
    });

    setTillLogout(100);
    reset();

    localStorage.setItem(
      sessionActionKey,
      JSON.stringify({
        type: 'still-here',
        timestamp: Date.now(),
      })
    );

    setTimeout(() => {
      localStorage.removeItem(sessionActionKey);
    }, 100);

    try {
      await oktaAuth.tokenManager.renew('accessToken');
      await oktaAuth.tokenManager.renew('idToken');
    } catch (error) {
      console.error('Failed to renew Okta tokens:', error);
    }
  };

  const modalBody = (
    <>
      <div style={{ marginBottom: '20px' }}>
        <ProgressBar
          aria-label='progress-bar-label'
          color='primary'
          label=''
          value={tillLogout}
        />
      </div>

      <TypographyStyled>
        You&apos;re being timed out due to inactivity. Please select an option
        below. Otherwise, you will be logged out automatically.
      </TypographyStyled>
    </>
  );

  return (
    <SharedModal
      button={{
        onClick: {
          primary: logout,
          secondary: () => handleCloseModal(true),
        },
        text: { primary: 'Log out', secondary: `I'm still here` },
      }}
      onClose={() => handleCloseModal(false)}
      show={showTimedOutModal}
      showCloseButton={false}
      size='sm'
      text={{
        body: modalBody,
        header: 'Session Timeout',
      }}
      headerStyle={{ paddingTop: themeSizes.lg }}
    />
  );
};

export default SessionTimeoutModal;
